import Phaser from "phaser";
import { Player } from "../models/player";
import { MapHandler } from "../handlers/map-handler";
import { WindowManager } from "../ui/window-manager";
import { InteractionManager } from "../managers/interaction-manager";
import { isMobile, isTablet } from "../utils/mobile-utils";
import { isAndroid } from "../utils/android-utils";
import { TILE_SIZE } from "../models/common";

/**
 * BaseGameScene serves as a shared base class for all game scenes (MainScene, MarketScene, etc.)
 * It provides common functionality to avoid duplication including:
 * - Player and other player management
 * - Camera setup and configuration
 * - UI layer and WindowManager initialization
 * - Position update loops
 * - Loot box interaction highlighting
 * - Cleanup and socket listener management
 */
export abstract class BaseGameScene extends Phaser.Scene {
  // Player references
  protected player?: Player;
  protected otherPlayers: { [key: string]: Player } = {};

  // Map and rendering
  protected mapHandler?: MapHandler;
  protected mapWidth: number = 64;
  protected mapHeight: number = 64;

  // UI and camera
  protected uiLayer?: Phaser.GameObjects.Container;
  protected windowManager?: WindowManager;
  protected uiCamera?: Phaser.Cameras.Scene2D.Camera;

  // Position tracking
  protected positionUpdateTimer?: number;
  protected lastSentPosition: { x: number; y: number } = { x: 0, y: 0 };

  // Interaction tracking
  protected interactionManager: InteractionManager = new InteractionManager();
  protected lastPlayerTileX: number = 0;
  protected lastPlayerTileY: number = 0;
  protected lastButtonLabel: string | null = null;
  protected lastLootWindowVisible: boolean = false;

  // Transitioning state
  protected isTransitioning: boolean = false;

  constructor(sceneKey: string) {
    super(sceneKey);
  }

  /**
   * Setup UI layer and WindowManager in a consistent way across all scenes
   */
  protected setupUILayer(): void {
    // Create UI Layer for windows
    this.uiLayer = this.add.container(0, 0);
    this.uiLayer.setDepth(100000);

    // Create WindowManager
    this.windowManager = new WindowManager(this, this.uiLayer);
    window.windowManager = this.windowManager;

    // Show UI buttons
    this.windowManager.showUIButtons(this);
  }

  /**
   * Setup camera to follow player with consistent configuration
   */
  protected setupCamera(mapWidthPixels?: number, mapHeightPixels?: number): void {
    if (!this.player) return;

    const width = mapWidthPixels || this.mapWidth * TILE_SIZE;
    const height = mapHeightPixels || this.mapHeight * TILE_SIZE;

    // Follow player with main camera
    this.cameras.main.startFollow(this.player.container);
    this.cameras.main.setBounds(0, 0, width, height);

    // Ignore UI layer
    this.cameras.main.ignore(this.uiLayer);

    // Configure zoom based on device type
    let zoom = isTablet() ? 2.2 : isMobile() ? 1.5 : 1.1;
    if (isAndroid()) {
      zoom *= 0.6;
    }
    this.cameras.main.setZoom(zoom);
    this.cameras.main.roundPixels = true;

    // Create UI camera for UI elements only
    this.uiCamera = this.cameras.add(0, 0, this.scale.width, this.scale.height);
    this.uiCamera.setScroll(0, 0);
    this.uiCamera.setBackgroundColor("rgba(0, 0, 0, 0)");
    this.uiCamera.roundPixels = true;

    // Make UI camera ignore all game objects except UI layer
    this.children.each((child: Phaser.GameObjects.GameObject) => {
      if (child !== this.uiLayer) {
        this.uiCamera!.ignore(child);
      }
    });

    // Handle window resize
    this.scale.on("resize", (gameSize: Phaser.Structs.Size) => {
      if (this.uiCamera) {
        this.uiCamera.setSize(gameSize.width, gameSize.height);
      }
    });
  }

  /**
   * Start the position update loop that broadcasts player position at intervals
   * Only sends if player has moved more than 1 pixel since last update
   */
  protected startPositionUpdateLoop(
    eventName: string = "player-position",
  ): void {
    if (!this.player) return;

    this.positionUpdateTimer = window.setInterval(() => {
      if (window.socket && this.player) {
        const dx = Math.abs(this.player.x - this.lastSentPosition.x);
        const dy = Math.abs(this.player.y - this.lastSentPosition.y);

        // Only send if moved > 1 pixel
        if (dx > 1 || dy > 1) {
          window.socket.emit(eventName, {
            x: this.player.x,
            y: this.player.y,
          });
          this.lastSentPosition.x = this.player.x;
          this.lastSentPosition.y = this.player.y;
        }
      }
    }, 100);
  }

  /**
   * Interpolate other players to their target positions smoothly
   */
  protected interpolateOtherPlayers(deltaSeconds: number): void {
    Object.values(this.otherPlayers).forEach((otherPlayer) => {
      otherPlayer.interpolateToTarget(deltaSeconds);
    });
  }

  /**
   * Add another player to the scene
   */
  protected addOtherPlayer(
    socketId: string,
    x: number,
    y: number,
    maxHealth: number = 100,
  ): Player {
    if (this.otherPlayers[socketId]) {
      return this.otherPlayers[socketId];
    }

    const otherPlayer = new Player(socketId, this, x, y, maxHealth, maxHealth);
    this.otherPlayers[socketId] = otherPlayer;

    // Ensure UI camera ignores this player's container
    if (this.uiCamera && otherPlayer.container) {
      this.uiCamera.ignore(otherPlayer.container);
    }

    return otherPlayer;
  }

  /**
   * Remove another player from the scene with fade-out animation
   */
  protected removeOtherPlayer(socketId: string): void {
    const otherPlayer = this.otherPlayers[socketId];
    if (otherPlayer) {
      this.tweens.add({
        targets: otherPlayer.container,
        alpha: 0,
        scaleY: 0.5,
        duration: 300,
        ease: "Power2.in",
        onComplete: () => {
          otherPlayer.container.destroy();
        },
      });

      delete this.otherPlayers[socketId];
    }
  }

  /**
   * Common cleanup logic for unregistering listeners and destroying objects
   * Called from shutdown() and stop()
   */
  protected commonCleanup(): void {
    // Clear position update timer
    if (this.positionUpdateTimer) {
      window.clearInterval(this.positionUpdateTimer);
    }

    // Destroy window manager
    if (this.windowManager) {
      this.windowManager.destroy();
      this.windowManager = undefined;
    }

    // Clear collections
    this.otherPlayers = {};
  }

  /**
   * Called when scene shuts down
   */
  shutdown(): void {
    this.commonCleanup();
  }

  /**
   * Called when scene stops
   */
  stop(): void {
    this.commonCleanup();
  }
}
